"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Play, Plus } from "lucide-react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";

import { usePlayer } from "@/context/player-context";
import { useAuth } from "@/context/auth-context";

export default function Home() {
  const [featured, setFeatured] = useState<any>(null);
  const [continueWatching, setContinueWatching] = useState<any[]>([]);
  const [recentMovies, setRecentMovies] = useState<any[]>([]);
  const [recentSeries, setRecentSeries] = useState<any[]>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { playFile, playMedia } = usePlayer();
  const { token } = useAuth();

  // Load media library
  useEffect(() => {
    fetch(`/api/media`)
      .then(res => res.json())
      .then(data => {
        if (data.length > 0) {
          setRecentMovies(data.filter((m: any) => m.type === 'MOVIE').slice(0, 6));
          setRecentSeries(data.filter((m: any) => m.type === 'TV' || m.type === 'ANIME').slice(0, 6));
          setFeatured(data[0]);
        }
      })
      .catch(console.error);
  }, []);

  // Load real watch history
  useEffect(() => {
    if (!token) return;
    fetch('/api/media/history', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then((history: any[]) => {
        const items = history
          .filter(h => !h.completed && h.position > 10)
          .slice(0, 6)
          .map(h => ({
            fileId: h.fileId,
            mediaId: h.file?.media?.id,
            title: h.file?.media?.title,
            posterPath: h.file?.media?.posterPath,
            type: h.file?.media?.type,
            progress: h.duration ? Math.round((h.position / h.duration) * 100) : 0,
            position: h.position,
            duration: h.duration,
            season: h.file?.seasonNumber,
            episode: h.file?.episodeNumber,
          }));
        setContinueWatching(items);
      })
      .catch(console.error);
  }, [token]);

  // Loop 10s logic
  const handleTimeUpdate = () => {
    if (videoRef.current && videoRef.current.currentTime >= 10) {
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div className="flex flex-col -mt-24">
      {/* Hero Section with 10s Video Loop */}
      <section className="relative w-full h-[85vh] min-h-[500px] flex flex-col justify-end pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent z-10" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-transparent z-10" />
          
          {/* If we had a stream URL, we'd use it here. Falling back to poster with slow zoom for now 
              but adding the video tag structure for when playback is ready. */}
          {featured?.streamUrl ? (
            <video 
              ref={videoRef}
              autoPlay 
              muted 
              playsInline 
              onTimeUpdate={handleTimeUpdate}
              className="w-full h-full object-cover brightness-50"
              src={featured.streamUrl}
            />
          ) : (
            mediaPosterOrMesh(featured)
          )}
        </div>

        <div className="container mx-auto px-6 lg:px-10 flex flex-col gap-6 max-w-5xl mr-auto ml-0">
          <div className="flex flex-col gap-2">
             <Badge variant="outline" className="w-fit bg-white/5 text-white/60 border-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
               {featured?.type === 'ANIME' ? 'Dernier Anime' : featured?.type === 'TV' ? 'Dernière Série' : 'Dernier Film'}
             </Badge>
             <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white leading-tight">
               {featured?.title || "Osmose"}
             </h1>
          </div>
          
          <p className="text-base text-white/50 max-w-lg line-clamp-3 font-normal leading-relaxed">
            {featured?.overview || "Reprenez là où vous vous étiez arrêté ou explorez les nouveaux ajouts de votre bibliothèque."}
          </p>

          <div className="flex items-center gap-3 pt-2">
            {featured ? (
              <Button 
                size="lg" 
                className="h-11 px-8 rounded-full bg-white text-black hover:bg-white/90 font-semibold gap-2 shadow-lg"
                onClick={() => playMedia(featured.id, featured.title)}
              >
                <Play className="h-4 w-4 fill-current" />
                Lecture
              </Button>
            ) : (
              <Link href="/library">
                <Button size="lg" className="h-11 px-8 rounded-full bg-white text-black hover:bg-white/90 font-semibold gap-2 shadow-lg">
                  <Play className="h-4 w-4 fill-current" />
                  Lecture
                </Button>
              </Link>
            )}
            <Link href="/requests">
              <Button size="lg" variant="secondary" className="h-11 px-8 rounded-full bg-white/10 text-white hover:bg-white/20 font-semibold gap-2 border-0 shadow-lg">
                <Plus className="h-4 w-4" />
                Ma Liste
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-6 lg:px-10 flex flex-col gap-14 pb-20 relative z-20">
        
        {/* 1. Continuer à regarder */}
        {continueWatching.length > 0 && (
          <section className="flex flex-col gap-5">
            <h3 className="text-xl font-semibold text-white/90">Continuer à regarder</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {continueWatching.map((item) => (
                <div
                  key={item.fileId}
                  onClick={() => playFile(item.fileId, item.title, [{ fileId: item.fileId, title: item.title }], 0)}
                  className="group relative flex flex-col gap-2.5 cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden rounded-xl bg-white/5 border border-white/5 transition-all group-hover:bg-white/10 group-hover:border-white/20">
                    {item.posterPath ? (
                      <img src={item.posterPath} alt={item.title} className="h-full w-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-xs text-white/10 font-bold">{item.title}</div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                      <div className="h-full bg-white transition-all shadow-[0_0_8px_white]" style={{ width: `${item.progress}%` }} />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                       <div className="h-10 w-10 rounded-full bg-white/90 flex items-center justify-center text-black shadow-xl scale-90 group-hover:scale-100 transition-transform">
                          <Play className="h-5 w-5 fill-current ml-0.5" />
                       </div>
                    </div>
                  </div>
                  <div className="px-1 space-y-0.5">
                    <h4 className="text-[13px] font-bold text-white/90 group-hover:text-white transition-colors line-clamp-1 truncate">{item.title}</h4>
                    <p className="text-[10px] font-medium text-white/40 uppercase tracking-wider">
                      {item.type !== 'MOVIE' && item.season ? `S${item.season} E${item.episode}` : item.type === 'MOVIE' ? 'Film' : ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 2. Récemment ajouté dans Film */}
        <section className="flex flex-col gap-5">
          <div className="flex items-center justify-between border-l-2 border-primary pl-4">
            <h3 className="text-xl font-semibold text-white/90">Récemment ajouté dans Films</h3>
            <Link href="/library?type=movie" className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/30 hover:text-white transition-colors">Explorer</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {recentMovies.length > 0 ? recentMovies.map((item) => (
               <MediaCardSmall key={item.id} item={item} playMedia={playMedia} />
            )) : emptySlots()}
          </div>
        </section>

        {/* 3. Récemment ajouté dans Séries */}
        <section className="flex flex-col gap-5">
          <div className="flex items-center justify-between border-l-2 border-primary pl-4">
            <h3 className="text-xl font-semibold text-white/90">Récemment ajouté dans Séries</h3>
            <Link href="/library?type=tv" className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/30 hover:text-white transition-colors">Explorer</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {recentSeries.length > 0 ? recentSeries.map((item) => (
               <MediaCardSmall key={item.id} item={item} playMedia={playMedia} />
            )) : emptySlots()}
          </div>
        </section>

      </div>
    </div>
  );
}

function MediaCardSmall({ item, playMedia }: { item: any, playMedia: any }) {
  const year = item.releaseDate ? new Date(item.releaseDate).getFullYear() : '2024';
  
  return (
    <div 
      onClick={() => playMedia(item.id, item.title)} 
      className="group relative flex flex-col gap-2 cursor-pointer"
    >
      <div className="relative aspect-[2/3] overflow-hidden rounded-[1.5rem] bg-black border border-white/5 transition-all duration-300 group-hover:scale-[1.02] group-hover:border-white/20 shadow-md">
        {item.posterPath ? (
          <img 
            src={item.posterPath} 
            alt={item.title} 
            className="h-full w-full object-cover transition-all duration-500 group-hover:blur-[2px] group-hover:opacity-60 group-hover:scale-105" 
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-white/10 p-4 text-center font-bold">{item.title}</div>
        )}

        {/* HOVER OVERLAY */}
        <div className="absolute inset-0 z-30 flex flex-col justify-end p-5 opacity-0 group-hover:opacity-100 transition-all duration-500">
           {/* Gradient for text readability */}
           <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent pointer-events-none" />
           
           <div className="relative z-40 flex flex-col gap-3 translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
              <div className="flex flex-col">
                 <h4 className="text-[11px] font-bold text-white line-clamp-2 leading-tight">{item.title}</h4>
                 <p className="text-[9px] font-medium text-white/50 mt-1">{year}</p>
              </div>
              <div className="w-full h-8 bg-white text-black text-[10px] font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-white/90 transition-all">
                <Play className="h-3 w-3 fill-current" />
                Lecture
              </div>
           </div>
        </div>
      </div>
      <h4 className="text-[12px] font-medium text-white/60 group-hover:opacity-0 transition-opacity line-clamp-1 px-1">{item.title}</h4>
    </div>
  );
}

function mediaPosterOrMesh(featured: any) {
  return featured?.posterPath ? (
    <img 
      src={featured.backdropPath || featured.posterPath} 
      alt={featured.title} 
      className="w-full h-full object-cover opacity-60 scale-105 animate-slow-zoom"
    />
  ) : (
    <div className="w-full h-full bg-black opacity-40" />
  );
}

function emptySlots() {
  return [1,2,3,4,5,6].map(i => (
    <div key={i} className="aspect-[2/3] rounded-xl bg-white/[0.02] border border-dashed border-white/5" />
  ));
}
